// frontend/src/components/admin/SettingsPage.js
import React, { useState, useEffect } from 'react';
import { Card, Form, InputNumber, Button, Spin, Alert, message } from 'antd';
import { SaveOutlined, FieldTimeOutlined } from '@ant-design/icons';
import { settingsService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const SettingsPage = () => {
    const [form] = Form.useForm();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    const canEdit = user?.role?.profile?.permissions?.actions?.settings?.canEditSettings;

    const getSettingSafe = async (key, defaultValue) => {
        try {
            const response = await settingsService.getSetting(key);
            // Validamos que la respuesta sea exitosa y tenga valor
            if (response.data && response.data.success && response.data.data) {
                return response.data.data.value;
            }
            return defaultValue;
        } catch (error) {
            // Si es 404 (No existe) o error de red, usamos el default
            // Solo logueamos advertencia para depuración, pero no rompemos el flujo.
            console.warn(`Configuración '${key}' no encontrada o error. Usando default: ${defaultValue}`);
            return defaultValue;
        }
    };

useEffect(() => {
        const fetchSettings = async () => {
            try {
                setLoading(true);
                setError(null);

                // --- CARGA ROBUSTA Y ESCALABLE ---
                // Ahora usamos Promise.all con nuestra función segura.
                // Si mañana agregas 10 configuraciones más, solo agregas líneas aquí.
                // NINGUNA fallará el proceso completo si no existe.
                
                const [
                    maxPhotosVal, 
                    inactivityVal, 
                    pollingVal, 
                    minPhotosVal // <--- La nueva, sin miedo al éxito
                ] = await Promise.all([
                    getSettingSafe('maxPhotosPerList', 30), // Default 30 si falla
                    getSettingSafe('sessionInactivityTimeoutMinutes', 15), // Default 15
                    getSettingSafe('notificationPollingIntervalSeconds', 120), // Default 120
                    getSettingSafe('minPhotosPerList', 6) // Default 6 (Aquí es donde se protege sola)
                ]);

                // Asignamos todo de golpe. Como getSettingSafe SIEMPRE devuelve algo (valor o default),
                // ya no necesitamos verificar success uno por uno aquí.
                form.setFieldsValue({
                    maxPhotosPerList: maxPhotosVal,
                    sessionInactivityTimeoutMinutes: inactivityVal,
                    notificationPollingIntervalSeconds: pollingVal,
                    minPhotosPerList: minPhotosVal
                });

            } catch (err) {
                // Este catch solo saltaría si hay un error catastrófico de código, no de red/API
                console.error('Error crítico al iniciar formulario:', err);
                setError('Error inesperado al cargar la configuración.');
            } finally {
                setLoading(false);
            }
        };

        if (canEdit) {
            fetchSettings();
        } else {
            setLoading(false);
            setError("No tiene permisos para ver esta sección.");
        }
    }, [form, canEdit]);
    
    const onFinish = async (values) => {
        try {
            setSaving(true);
            setError(null);
            setSuccessMessage(null);

            const { maxPhotosPerList, sessionInactivityTimeoutMinutes, notificationPollingIntervalSeconds, minPhotosPerList } = values;

            // --- PASO 2: AÑADIR LA NUEVA CONFIGURACIÓN AL GUARDADO ---
            // Guardar todas las configuraciones en paralelo.
            await Promise.all([
                settingsService.updateSetting('maxPhotosPerList', maxPhotosPerList),
                settingsService.updateSetting('sessionInactivityTimeoutMinutes', sessionInactivityTimeoutMinutes),
                settingsService.updateSetting('notificationPollingIntervalSeconds', notificationPollingIntervalSeconds), // Se guarda la nueva configuración
                settingsService.updateSetting('minPhotosPerList', minPhotosPerList)//actualizacion minfotos
            ]);

            setSuccessMessage('¡Configuración guardada exitosamente!');
            message.success('Los cambios se han guardado.'); // Feedback inmediato
        } catch (err) {
            console.error('Error al guardar la configuración:', err);
            setError(err.response?.data?.message || 'No se pudo guardar la configuración.');
        } finally {
            setSaving(false);
        }
    };

    const handleFormChange = () => {
        if (successMessage) setSuccessMessage(null);
        if (error) setError(null);
    };

    if (!canEdit && !loading) {
        return <Alert message="Acceso Denegado" description="No tiene los permisos necesarios para gestionar la configuración." type="error" showIcon />;
    }

    return (
        <Card title="Configuración General del Sistema">
            <Spin spinning={loading} tip="Cargando configuración...">
                {error && !loading ? (
                    <Alert message="Error" description={error} type="error" showIcon />
                ) : (
                    <>
                        {successMessage && (
                            <Alert
                                message="Éxito"
                                description={successMessage}
                                type="success"
                                showIcon
                                closable
                                onClose={() => setSuccessMessage(null)}
                                style={{ marginBottom: '24px' }}
                            />
                        )}
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onFinish}
                            onValuesChange={handleFormChange}
                            style={{ maxWidth: 400 }}
                        >


                            {/* +++ NUEVO CAMPO: MÍNIMO DE FOTOS +++ */}
                            <Form.Item
                                name="minPhotosPerList"
                                label="Mínimo de fotos de evidencia requeridas"
                                dependencies={['maxPhotosPerList']} // Para validar contra el máximo
                                rules={[
                                    { required: true, message: 'Requerido.' },
                                    { type: 'integer', message: 'Debe ser entero.' },
                                    { type: 'number', min: 1, message: 'Mínimo 1.' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || value <= getFieldValue('maxPhotosPerList')) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('El mínimo no puede ser mayor que el límite máximo.'));
                                        },
                                    }),
                                ]}
                                tooltip="Cantidad mínima de fotos que el operario debe tomar para poder guardar el listado."
                            >
                                <InputNumber style={{ width: '100%' }} placeholder="Ej: 6" precision={0} readOnly={!canEdit} />
                            </Form.Item>
                            {/* +++ FIN NUEVO CAMPO +++ */}

                            <Form.Item
                                name="maxPhotosPerList"
                                label="Límite máximo de fotos por listado"
                                rules={[
                                    { required: true, message: 'Este campo es obligatorio.' },
                                    { type: 'integer', message: 'El valor debe ser un número entero.' },
                                    { type: 'number', min: 1, max: 100, message: 'El valor debe estar entre 1 y 100.' }
                                ]}
                                tooltip="Define cuántas fotos de evidencia se pueden subir como máximo en un solo listado de empaque."
                            >
                                <InputNumber style={{ width: '100%' }} placeholder="Ej: 30" precision={0} readOnly={!canEdit} />
                            </Form.Item>

                            <Form.Item
                                name="sessionInactivityTimeoutMinutes"
                                label="Tiempo de inactividad de sesión (minutos)"
                                rules={[
                                    { required: true, message: 'Este campo es obligatorio.' },
                                    { type: 'integer', message: 'El valor debe ser un número entero.' },
                                    { type: 'number', min: 15, message: 'El mínimo debe ser 15 minutos.' }
                                ]}
                                tooltip="Cierra la sesión de un usuario si no hay actividad después de este tiempo. Se recomienda un mínimo de 15 minutos."
                            >
                                <InputNumber style={{ width: '100%' }} placeholder="Ej: 240" precision={0} readOnly={!canEdit} />
                            </Form.Item>

                            {/* +++ INICIO: NUEVO CAMPO PARA INTERVALO DE POLLING +++ */}
                            <Form.Item
                                name="notificationPollingIntervalSeconds"
                                label="Intervalo de sondeo de notificaciones (segundos)"
                                rules={[
                                    { required: true, message: 'Este campo es obligatorio.' },
                                    { type: 'integer', message: 'El valor debe ser un número entero.' },
                                    { type: 'number', min: 0, message: 'El valor mínimo es 0.' }
                                ]}
                                tooltip="Frecuencia con la que el sistema busca nuevas notificaciones. Un valor de 0 deshabilita la comprobación automática."
                            >
                                <InputNumber
                                    addonAfter="segundos"
                                    style={{ width: '100%' }}
                                    placeholder="Ej: 120"
                                    precision={0}
                                    readOnly={!canEdit}
                                />
                            </Form.Item>
                            {/* +++ FIN: NUEVO CAMPO PARA INTERVALO DE POLLING +++ */}

                            {canEdit && (
                                <Form.Item>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        icon={<SaveOutlined />}
                                        loading={saving}
                                    >
                                        Guardar Cambios
                                    </Button>
                                </Form.Item>
                            )}
                        </Form>
                    </>
                )}
            </Spin>
        </Card>
    );
};

export default SettingsPage;
